import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import { Button } from '@THC/components/Button';
import { CrossIcon } from '@THC/components/Icons/Cross';
import { MenuIcon } from '@THC/components/Icons/Menu';
import { PencilIcon } from '@THC/components/Icons/Pencil';
import { LoadingIndicator } from '@THC/components/LoadingIndicator';
import { MDText } from 'i18n-react';
import { useStore } from 'zustand';

import type { ConversationSummary } from 'Services/motorway/services/dealerAgent/dealerAgent.types';
import VersionBadge from 'Storybook/local/VersionBadge/VersionBadge';

import LocalTexts from '../../AiCompanion.texts';
import { ConversationStore } from '../../services';

import styles from './ConversationSidebar.module.scss';

const LocalT = new MDText(LocalTexts);

interface ConversationSidebarProps {
	onNewConversation: () => void;
	onSelectConversation: (conversationId: string) => void;
}

const ConversationList: FC<{
	activeConversationId: Nullable<string>;
	conversations: ConversationSummary[];
	loading: boolean;
	onSelectConversation: (conversationId: string) => void;
}> = ({ activeConversationId, conversations, loading, onSelectConversation }) => {
	if (loading && !conversations.length) {
		return (
			<div className={styles.loadingItem}>
				<LoadingIndicator data-spinner-busy variant="spinner" />
			</div>
		);
	}

	if (!conversations.length) {
		return null;
	}

	return (
		<ul className={styles.list} role="list">
			{conversations.map((conversation: ConversationSummary) => {
				const isActive = conversation.conversationId === activeConversationId;

				return (
					<li key={conversation.conversationId} className={isActive ? styles.activeItem : styles.item}>
						<Button
							data-thc-button
							fullWidth
							aria-current={isActive ? 'true' : undefined}
							disabled={isActive}
							label={conversation.title}
							onClick={() => onSelectConversation(conversation.conversationId)}
							variant="tertiary"
						/>
					</li>
				);
			})}
		</ul>
	);
};

export const ConversationSidebar: FC<ConversationSidebarProps> = ({ onNewConversation, onSelectConversation }) => {
	const { activeConversationId, conversations, loading } = useStore(ConversationStore);
	const [isMobileOpen, setIsMobileOpen] = useState(false);

	useEffect(() => {
		document.body.style.overflow = isMobileOpen ? 'hidden' : '';

		return () => {
			document.body.style.overflow = '';
		};
	}, [isMobileOpen]);

	const sortedConversations = [...conversations].sort(
		(a, b) => new Date(b.lastMessageAt ?? b.createdAt).getTime() - new Date(a.lastMessageAt ?? a.createdAt).getTime(),
	);

	const handleMobileSelect = useCallback(
		(conversationId: string) => {
			onSelectConversation(conversationId);
			setIsMobileOpen(false);
		},
		[onSelectConversation],
	);

	const handleMobileNewChat = useCallback(() => {
		onNewConversation();
		setIsMobileOpen(false);
	}, [onNewConversation]);

	return (
		<>
			{/* Desktop sidebar */}
			<aside
				aria-label={LocalT.translate('sidebar.title') as string}
				className={cx(styles.desktopWrapper, activeConversationId && styles.sticky)}
			>
				<div className={styles.newChatButton}>
					<Button
						data-thc-button
						reverse
						disabled={!activeConversationId}
						icon={PencilIcon}
						label={LocalT.translate('sidebar.newChat') as string}
						onClick={onNewConversation}
						size="small"
						variant="secondary"
					/>
				</div>
				<span className={styles.emptyStateHeading}>{LocalT.translate('sidebar.chatsHeading')}</span>
				{!loading && !sortedConversations.length && (
					<span className={styles.emptyStateText}>{LocalT.translate('sidebar.emptyState')}</span>
				)}
				<ConversationList
					activeConversationId={activeConversationId}
					conversations={sortedConversations}
					loading={loading}
					onSelectConversation={onSelectConversation}
				/>
			</aside>

			{/* Mobile menu button */}
			<div className={styles.mobileMenuButton}>
				<Button
					icon={MenuIcon}
					label="Menu"
					onClick={() => setIsMobileOpen(true)}
					showLabel={false}
					variant="secondary"
				/>
			</div>

			{/* Mobile drawer */}
			<div className={isMobileOpen ? styles.mobileDrawer : styles.mobileDrawerHidden}>
				<div className={styles.mobileHeader}>
					<Button
						icon={CrossIcon}
						label="Close"
						onClick={() => setIsMobileOpen(false)}
						showLabel={false}
						variant="tertiary"
					/>
				</div>
				<div className={styles.mobileContent}>
					<div className={styles.mobileChatsRow}>
						<span className={styles.mobileChatsHeading}>{LocalT.translate('sidebar.chatsHeading')}</span>
						<VersionBadge text="Beta" />
					</div>
					<div className={styles.mobileScrollArea}>
						{!loading && !sortedConversations.length && <span>{LocalT.translate('sidebar.emptyState')}</span>}
						<ConversationList
							activeConversationId={activeConversationId}
							conversations={sortedConversations}
							loading={loading}
							onSelectConversation={handleMobileSelect}
						/>
					</div>
					<div className={styles.mobileFooter}>
						<Button
							fullWidth
							disabled={!activeConversationId}
							label={LocalT.translate('sidebar.startNewChat') as string}
							onClick={handleMobileNewChat}
							variant="secondary"
						/>
					</div>
				</div>
			</div>
		</>
	);
};
